import React, { useCallback, useMemo } from 'react';
import cl from 'classnames';
import isEmpty from 'lodash/isEmpty';
import type { ComposerProductInput } from '@vellnes/composer-react';

import { SelectedProductsSidebarFooterProductsSet } from './SelectedProductsSidebarFooter.types';
import {
  ProductsSetSelectedProductsID,
  UpdateProductsSetCacheAction
} from '../../../../../../productsSets/productsSetsTypes';
import { TeamNanoID, TeamUUID } from '../../../../../../teams/teamsTypes';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { DownloadProductsSetButton } from '../../../../../../productsSets/components/buttons/DownloadProductsSetButton';
import { DownloadProductsSetSeparatelyButton } from '../../../../../../productsSets/components/buttons/DownloadProductsSetSeparatelyButton';
import { ProductsListOnProductsSelect } from '../../../../../../products/components/lists/ProductsList';
import { SelectedProductsSidebarActionsPopover } from '../SelectedProductsSidebarActionsPopover';
import { SelectedProductsSidebarAttachModalButton } from '../SelectedProductsSidebarAttachModalButton';
import { ComposeSelectedProductsModalButton } from '../../../../../../composer/components/ComposeSelectedProductsModalButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';

import { composerKeys, words } from '../../../../../../../locales/keys';

import { ProductsSetsPermissions } from '../../../../../../productsSets/productsSetsConstants';
import { IconsEnum } from '../../../../../../../assets/icons/types';

const selectedProductsSidebarActionsPermissions = [
  ProductsSetsPermissions.READ_PRODUCTS_SET_CLEAR_BUTTON
];

interface SelectedProductsSidebarFooterProps {
  productsSet: SelectedProductsSidebarFooterProductsSet;
  checkedSelectedProductIds: ProductsSetSelectedProductsID[];
  onCustomAttach?: ProductsListOnProductsSelect;
  disabledAttach?: boolean;
  onClose: () => void;
  updateProductsSetCache: UpdateProductsSetCacheAction<SelectedProductsSidebarFooterProductsSet>;
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
}

function SelectedProductsSidebarFooter({
  productsSet,
  checkedSelectedProductIds,
  onCustomAttach,
  disabledAttach,
  onClose,
  updateProductsSetCache,
  companyNanoId,
  companyUuid
}: SelectedProductsSidebarFooterProps) {
  const currentUser = useCurrentUser();

  const handleCustomAttach = useCallback(() => {
    if (onCustomAttach && productsSet) {
      const checkedProducts = productsSet.selectedProducts?.filter((product) =>
        checkedSelectedProductIds?.includes(product.id)
      );

      onCustomAttach(checkedProducts);
    }
  }, [onCustomAttach, productsSet, checkedSelectedProductIds]);

  const withActionsPopover = selectedProductsSidebarActionsPermissions.some(
    (action) => currentUser.hasPermissions(action)
  );

  const checkedProducts = useMemo(
    () =>
      (productsSet.selectedProducts || []).filter((selectedProduct) =>
        checkedSelectedProductIds?.includes(selectedProduct.id)
      ),
    [productsSet, checkedSelectedProductIds]
  );

  // Compose needs every checked product to have a 3D model — if even one is
  // missing its GLB, disable the button up front instead of silently opening
  // Composer with fewer products than the user selected.
  const hasProductWithoutModel = useMemo(
    () =>
      checkedProducts.some(
        (selectedProduct) => !selectedProduct.product.previewModel?.file
      ),
    [checkedProducts]
  );

  const composerProducts = useMemo<ComposerProductInput[]>(
    () =>
      checkedProducts
        .filter((selectedProduct) => selectedProduct.product.previewModel?.file)
        .map((selectedProduct) => ({
          id: String(selectedProduct.product.nanoId),
          name: selectedProduct.product.name,
          glbUrl: selectedProduct.product.previewModel.file
        })),
    [checkedProducts]
  );

  return (
    <div className="justify-between flex flex-wrap gap-2">
      <CheckPermissions
        action={ProductsSetsPermissions.READ_PRODUCTS_SET_COMPOSE_BUTTON}
      >
        <ComposeSelectedProductsModalButton
          className="py-2 pl-4 pr-4 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus-visible:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          dataGa="compose-selected-products-modal-button"
          i18nText={composerKeys.composeButton}
          disabled={isEmpty(composerProducts) || hasProductWithoutModel}
          tooltipI18nText={
            hasProductWithoutModel
              ? composerKeys.composeDisabledMissingModel
              : undefined
          }
          products={composerProducts}
          companyNanoId={companyNanoId}
          companyUuid={companyUuid}
        />
      </CheckPermissions>

      <CheckPermissions
        action={ProductsSetsPermissions.READ_PRODUCTS_SET_DOWNLOAD_BUTTON}
      >
        <DownloadProductsSetButton
          className="font-medium hover:bg-gray-200 items-center px-2 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
          productsSetUuid={productsSet.uuid}
        />
      </CheckPermissions>

      <CheckPermissions
        action={
          ProductsSetsPermissions.READ_PRODUCTS_SET_DOWNLOAD_SEPARATELY_BUTTON
        }
      >
        <DownloadProductsSetSeparatelyButton
          className="font-medium hover:bg-gray-200 items-center px-2 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
          dataGa="download-products-set-separately-button"
          tooltipI18nText={words.download}
          icon={IconsEnum.DOWNLOAD_SOLID}
          productsSetUuid={productsSet.uuid}
        />
      </CheckPermissions>

      <span className="grow relative z-0 inline-flex justify-end shadow-sm rounded-md">
        {onCustomAttach ? (
          <PureButtonHelper
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            dataGa="selected-products-set-attach-button"
            i18nText={words.attach}
            onClick={handleCustomAttach}
            disabled={isEmpty(checkedSelectedProductIds) || disabledAttach}
          />
        ) : (
          <SelectedProductsSidebarAttachModalButton
            optimisticUpdate={productsSet.optimisticUpdate}
            selectedProductIds={checkedSelectedProductIds}
            onClose={onClose}
            updateProductsSetCache={updateProductsSetCache}
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            disabled={isEmpty(checkedSelectedProductIds) || disabledAttach}
          />
        )}

        {withActionsPopover && (
          <SelectedProductsSidebarActionsPopover
            productsSet={productsSet}
            onClose={onClose}
            updateProductsSetCache={updateProductsSetCache}
          />
        )}
      </span>
    </div>
  );
}

export default SelectedProductsSidebarFooter;
